import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Icon, Typography, IconButton, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Dialog, Divider } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { closeDialog, openDialog } from 'app/store/actions/fuse/dialog.actions';

const applyDisabledToChildren = (child, isDisabled) => {
    if (React.isValidElement(child)) {
        const shouldRemainEnabled = typeof child.props.id === 'string' && child.props.id.startsWith("script-actions-view");

        return React.cloneElement(child, {
            disabled: !shouldRemainEnabled && isDisabled,
            children: child.props.children
                ? React.Children.map(child.props.children, (nestedChild) =>
                    applyDisabledToChildren(nestedChild, isDisabled)
                )
                : null,
        });
    }
    return child;
};

export const DisabledFormWrapper = ({ children, isDisabled }) => {
    const renderedChildren = React.Children.map(children, (child) =>
        applyDisabledToChildren(child, isDisabled)
    );

    return <div className="w-5/6">{renderedChildren}</div>;
};

// Deployment Failure Dialog Component
const DeploymentFailureDialog = ({ cluster }) => {
    const dispatch = useDispatch();

    const handleClose = () => {
        dispatch(closeDialog());
    };

    const handleViewLogs = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Import needed for logs
        import('../../main/cluster/store/actions').then(Actions => {
            import('../../main/cluster/Logs').then(LogsModule => {
                const Logs = LogsModule.default;
                const type = 'deploy'; // Show deployment logs
                
                // Close current dialog
                dispatch(closeDialog());
                
                // Fetch logs and open logs dialog
                dispatch(Actions.getLogs({ id: cluster.id, type }))
                    .then(() => {
                        dispatch(openDialog({
                            children: <Logs cluster={cluster} type={type} />,
                            maxWidth: 'lg',
                            fullWidth: true
                        }));
                    })
                    .catch(err => {
                        console.error('Error fetching logs:', err);
                    });
            });
        });
    };

    return (
        <React.Fragment>
            <DialogTitle>
                <div className="flex items-center">
                    <Icon style={{ color: '#f44336', fontSize: '28px', marginRight: '12px' }}>
                        error_outline
                    </Icon>
                    <Typography variant="h6" component="span" className="font-bold">
                        Deployment Failed
                    </Typography>
                </div>
            </DialogTitle>
            <DialogContent className="p-24">
                {/* Error Type Badge */}
                <div 
                    style={{
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        display: 'inline-block',
                        marginBottom: '16px'
                    }}
                >
                    <Typography 
                        variant="caption" 
                        style={{ 
                            color: '#f44336',
                            fontWeight: 'bold',
                            fontSize: '12px'
                        }}
                    >
                        DEPLOYMENT ERROR
                    </Typography>
                </div>

                <Typography variant="body1" paragraph style={{ marginBottom: '24px' }}>
                    This cluster failed during deployment and was automatically destroyed.
                </Typography>
                
                {cluster.info && cluster.info.deploymentFailure && cluster.info.deploymentFailure.reason && (
                    <div style={{ marginBottom: '24px' }}>
                        <Typography variant="body2" color="textSecondary" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            Error Details:
                        </Typography>
                        <pre style={{
                            backgroundColor: '#fff5f5',
                            color: '#c62828',
                            padding: '16px',
                            borderRadius: '8px',
                            overflow: 'auto',
                            fontSize: '13px',
                            fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace',
                            border: '1px solid #ffcdd2',
                            margin: 0,
                            maxHeight: '300px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: '1.5'
                        }}>
                            <code>{cluster.info.deploymentFailure.reason}</code>
                        </pre>
                    </div>
                )}

                {cluster.info && cluster.info.deploymentFailure && cluster.info.deploymentFailure.failedAt && (
                    <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                        <strong>Failed At:</strong> {new Date(cluster.info.deploymentFailure.failedAt).toLocaleString()}
                    </Typography>
                )}

                <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                    <strong>Recommended Actions:</strong>
                </Typography>
                <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                    <li><Typography variant="body2">Check deployment logs for detailed error information</Typography></li>
                    <li><Typography variant="body2">Verify cloud provider credentials and permissions</Typography></li>
                    <li><Typography variant="body2">Ensure resource quotas are not exceeded</Typography></li>
                    <li><Typography variant="body2">Contact support if the issue persists</Typography></li>
                </ul>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleViewLogs} color="primary">
                    View Full Logs
                </Button>
                <Button onClick={handleClose} color="primary" autoFocus>
                    Close
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

// Offline Cluster Dialog Component
const OfflineClusterDialog = () => {
    const dispatch = useDispatch();

    const handleClose = () => {
        dispatch(closeDialog());
    };

    return (
        <React.Fragment>
            <DialogTitle>
                <Typography className="font-bold text-gray-600">Cluster Offline</Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    This cluster is currently offline and not responding to health checks.
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    <strong>Possible causes:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div">
                    <ul style={{ paddingLeft: '20px' }}>
                        <li>Network connectivity issues</li>
                        <li>Cluster infrastructure problems</li>
                        <li>Head node is down or unresponsive</li>
                        <li>Service interruption on the cloud provider</li>
                    </ul>
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                    <strong>Recommended actions:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div">
                    <ul style={{ paddingLeft: '20px' }}>
                        <li>Check your network connection</li>
                        <li>Verify cloud provider service status</li>
                        <li>Review cluster logs for errors</li>
                        <li>Contact support if the issue persists</li>
                    </ul>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

// Degraded Cluster Dialog Component
const DegradedClusterDialog = ({ healthData }) => {
    const dispatch = useDispatch();

    const handleClose = () => {
        dispatch(closeDialog());
    };

    const formatHealthStats = (healthData) => {
        if (!healthData || !healthData.system) return null;

        const { system } = healthData;
        return {
            memory: system.memory ? `${system.memory.usagePercent}%` : 'N/A',
            disk: system.disk ? `${system.disk.usagePercent}%` : 'N/A',
            load: system.loadAverage ? parseFloat(system.loadAverage['1min']).toFixed(2) : 'N/A',
            uptime: system.uptime ? `${Math.floor(system.uptime / 3600)} hours` : 'N/A'
        };
    };

    const stats = formatHealthStats(healthData);
    const issues = healthData?.issues || [];

    return (
        <React.Fragment>
            <DialogTitle>
                <Typography className="font-bold text-orange-600">Cluster Degraded</Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    This cluster is operational but experiencing performance issues.
                </Typography>
                
                {issues.length > 0 && (
                    <>
                        <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                            <strong>Current Issues:</strong>
                        </Typography>
                        <Typography variant="body2" color="error" component="div">
                            <ul style={{ paddingLeft: '20px' }}>
                                {issues.map((issue, index) => (
                                    <li key={index}>{issue}</li>
                                ))}
                            </ul>
                        </Typography>
                    </>
                )}

                {stats && (
                    <>
                        <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                            <strong>Current Stats:</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="div">
                            <ul style={{ paddingLeft: '20px' }}>
                                <li>Memory Usage: {stats.memory}</li>
                                <li>Disk Usage: {stats.disk}</li>
                                <li>Load Average: {stats.load}</li>
                                <li>Uptime: {stats.uptime}</li>
                            </ul>
                        </Typography>
                    </>
                )}

                <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                    <strong>Recommendations:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" component="div">
                    <ul style={{ paddingLeft: '20px' }}>
                        <li>Monitor cluster performance closely</li>
                        <li>Consider reducing workload if possible</li>
                        <li>Check for resource-intensive jobs</li>
                        <li>Scale up resources if issues persist</li>
                    </ul>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

export const ClusterState = ({ state, className, healthData }) => {
    const dispatch = useDispatch();
    const healthServiceStatus = useSelector(({ settings }) => settings.settings.healthServiceStatus);
    const isHealthServiceEnabled = healthServiceStatus?.enabled !== false;
    const isHealthAlertsEnabled = healthServiceStatus?.alertsEnabled !== false;
    const shouldShowHealthAlerts = isHealthServiceEnabled && isHealthAlertsEnabled;
    const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
    
    const showOfflineDialog = () => {
        dispatch(openDialog({
            children: <OfflineClusterDialog />,
            maxWidth: 'sm',
            fullWidth: true,
            onClose: (event, reason) => {
                if (reason !== 'backdropClick') {
                    dispatch(closeDialog());
                }
            }
        }));
    };

    const showDegradedDialog = () => {
        dispatch(openDialog({
            children: <DegradedClusterDialog healthData={healthData} />,
            maxWidth: 'sm',
            fullWidth: true,
            onClose: (event, reason) => {
                if (reason !== 'backdropClick') {
                    dispatch(closeDialog());
                }
            }
        }));
    };

        const getIconAndStateText = () => {
        switch (state) {
            case CLUSTER_STATES.DEPLOYED:
                // Check if cluster is degraded (available but with health issues) - only if health service and alerts are enabled
                if (shouldShowHealthAlerts && healthData && healthData.status === 'degraded') {
                    const issues = healthData.issues || [];
                    const tooltipText = issues.length > 0 ? issues.join(', ') : 'Cluster is experiencing performance issues';
                    
                    return { 
                        icon: <Icon style={{ color: 'green', fontSize: '20px', verticalAlign: 'middle' }} >cloud_upload</Icon>, 
                        stateText: capitalize(state),
                        warning: true,
                        warningIcon: (
                            <Tooltip title={tooltipText} arrow>
                                <IconButton size="small" onClick={showDegradedDialog} style={{ padding: '4px', marginLeft: '8px' }}>
                                    <Icon style={{ color: 'orange', fontSize: '20px' }}>warning</Icon>
                                </IconButton>
                            </Tooltip>
                        )
                    };
                }
                return { icon: <Icon style={{ color: 'green', fontSize: '20px', verticalAlign: 'middle' }} >cloud_upload</Icon>, stateText: capitalize(state) };
            case CLUSTER_STATES.DESTROYED:
                return { icon: <Icon style={{ color: 'red', fontSize: '20px', verticalAlign: 'middle' }} >cloud_download</Icon>, stateText: capitalize(state) };
            case CLUSTER_STATES.STOPPED:
                return { icon: <Icon style={{ color: 'grey', fontSize: '20px', verticalAlign: 'middle' }} >cloud_download</Icon>, stateText: capitalize(state) };
            case CLUSTER_STATES.OFFLINE:
                // Always show offline state correctly, but only show warning icon if alerts are enabled
                if (shouldShowHealthAlerts) {
                    return { 
                        icon: <Icon style={{ color: 'grey', fontSize: '20px', verticalAlign: 'middle' }} >cloud_off</Icon>, 
                        stateText: 'Offline',
                        warning: true,
                        warningIcon: (
                            <Tooltip title="Cluster is offline and not responding to health checks" arrow>
                                <IconButton size="small" onClick={showOfflineDialog} style={{ padding: '4px', marginLeft: '8px' }}>
                                    <Icon style={{ color: 'grey', fontSize: '20px' }}>warning</Icon>
                                </IconButton>
                            </Tooltip>
                        )
                    };
                } else {
                    // If health alerts are disabled, show offline state without warning icon
                    return { icon: <Icon style={{ color: 'grey', fontSize: '20px', verticalAlign: 'middle' }} >cloud_off</Icon>, stateText: 'Offline' };
                }
            case CLUSTER_STATES.DEPLOYING:
            case CLUSTER_STATES.DESTROYING:
            case CLUSTER_STATES.STARTING:
            case CLUSTER_STATES.STOPPING:
                return { icon: <CircularProgress size={20} />, stateText: capitalize(state) };
            case CLUSTER_STATES.FAILED:
                return { icon: <Icon style={{ color: 'red', fontSize: '20px', verticalAlign: 'middle' }} >cloud_off</Icon>, stateText: capitalize(state) };
            default:
                return { icon: <Icon style={{ color: 'grey', fontSize: '20px', verticalAlign: 'middle' }} >error_outline</Icon>, stateText: 'Invalid State' };
        }
    };

    const { icon, stateText, warning, warningIcon } = getIconAndStateText();

    return (
        <div className={`flex items-center ${className}`}>
            {icon}
            <Typography className='font-bold ml-2' variant="body2" style={{ display: 'flex', alignItems: 'center' }}>
                {stateText}
            </Typography>
            {warning && warningIcon && (
                <div className="flex items-center pl-2">{warningIcon}</div>
            )}
        </div>
    );
};

// Deployment Failure Indicator Component
export const DeploymentFailureIndicator = ({ cluster }) => {
    const dispatch = useDispatch();
    
    if (!cluster || !cluster.info || !cluster.info.deploymentFailure || !cluster.info.deploymentFailure.failed) {
        return null;
    }

    const showDeploymentFailureDialog = () => {
        dispatch(openDialog({
            children: <DeploymentFailureDialog cluster={cluster} />,
            maxWidth: 'sm',
            fullWidth: true,
            onClose: (event, reason) => {
                if (reason !== 'backdropClick') {
                    dispatch(closeDialog());
                }
            }
        }));
    };

    return (
        <IconButton size="small" onClick={showDeploymentFailureDialog} style={{ padding: '4px', marginLeft: '8px' }}>
            <Icon style={{ color: '#ff9800', fontSize: '20px' }}>warning</Icon>
        </IconButton>
    );
};

// Destroy Failure Dialog Component
const DestroyFailureDialog = ({ cluster }) => {
    const dispatch = useDispatch();

    const handleClose = () => {
        dispatch(closeDialog());
    };

    const handleViewLogs = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Import needed for logs
        import('../../main/cluster/store/actions').then(Actions => {
            import('../../main/cluster/Logs').then(LogsModule => {
                const Logs = LogsModule.default;
                const type = 'destroy'; // Show destroy logs
                
                // Close current dialog
                dispatch(closeDialog());
                
                // Fetch logs and open logs dialog
                dispatch(Actions.getLogs({ id: cluster.id, type }))
                    .then(() => {
                        dispatch(openDialog({
                            children: <Logs cluster={cluster} type={type} />,
                            maxWidth: 'lg',
                            fullWidth: true
                        }));
                    })
                    .catch(err => {
                        console.error('Error fetching logs:', err);
                    });
            });
        });
    };

    return (
        <React.Fragment>
            <DialogTitle>
                <Typography className="font-bold text-red-700">Destroy Operation Failed</Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    This cluster failed to destroy after {cluster.info?.destroyFailure?.retryCount || 0} retry attempt(s).
                </Typography>
                
                {cluster.info?.destroyFailure?.reason && (
                    <>
                        <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                            <strong>Failure Reason:</strong>
                        </Typography>
                        <Typography variant="body2" color="error">
                            {cluster.info.destroyFailure.reason}
                        </Typography>
                    </>
                )}
                
                {cluster.info?.destroyFailure?.failedAt && (
                    <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                        <strong>Failed At:</strong> {new Date(cluster.info.destroyFailure.failedAt).toLocaleString()}
                    </Typography>
                )}
                
                <Typography variant="body2" color="textSecondary" paragraph style={{ marginTop: '16px' }}>
                    <strong>Recommended Actions:</strong>
                </Typography>
                <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                    <li><Typography variant="body2">Check destroy logs for detailed error information</Typography></li>
                    <li><Typography variant="body2">Verify provider credentials and permissions</Typography></li>
                    <li><Typography variant="body2">Check for resource locks or dependencies</Typography></li>
                    <li><Typography variant="body2">Manual cleanup may be required via provider console</Typography></li>
                    <li><Typography variant="body2">Contact administrator if issue persists</Typography></li>
                </ul>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleViewLogs} color="primary">
                    View Logs
                </Button>
                <Button onClick={handleClose} color="primary" autoFocus>
                    Close
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

// Destroy Failure Indicator Component
export const DestroyFailureIndicator = ({ cluster }) => {
    const dispatch = useDispatch();
    
    if (!cluster || !cluster.info || !cluster.info.destroyFailure || !cluster.info.destroyFailure.failed) {
        return null;
    }

    const showDestroyFailureDialog = () => {
        dispatch(openDialog({
            children: <DestroyFailureDialog cluster={cluster} />,
            maxWidth: 'sm',
            fullWidth: true,
            onClose: (event, reason) => {
                if (reason !== 'backdropClick') {
                    dispatch(closeDialog());
                }
            }
        }));
    };

    const tooltipText = (cluster.info && cluster.info.destroyFailure && cluster.info.destroyFailure.reason) ? cluster.info.destroyFailure.reason : 'Destroy failed after all retry attempts - click for details';

    return (
        <Tooltip title={String(tooltipText)} arrow>
            <IconButton size="small" onClick={showDestroyFailureDialog} style={{ padding: '4px', marginLeft: '8px' }}>
                <Icon style={{ color: '#d32f2f', fontSize: '20px' }}>warning</Icon>
            </IconButton>
        </Tooltip>
    );
};

export const ClusterActionTime = ({ state, destroytime, deploytime, deploytimer, destroytimer, storageDeploying }) => {
    // Show storage deploying message for AVAILABLE state with VolumeZ storage deploying
    if (storageDeploying && state === CLUSTER_STATES.DEPLOYED) {
        return (
            <div className="mt-1">
                <div style={{ 
                    backgroundColor: 'rgba(33, 150, 243, 0.08)', 
                    borderRadius: '3px', 
                    padding: '1px 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '10px',
                    gap: '3px'
                }}>
                    <span style={{ color: '#1976d2', fontWeight: '500' }}>Deploying storage</span>
                    <Tooltip 
                        title="The cluster is deployed and available. High-performance storage is still deploying in the background."
                        placement="top"
                    >
                        <Icon style={{ fontSize: '12px', color: '#1976d2', cursor: 'help' }}>info</Icon>
                    </Tooltip>
                </div>
            </div>
        );
    }
    
    if (state === CLUSTER_STATES.DEPLOYED || state === CLUSTER_STATES.DESTROYED) {
        // Only render if at least one time exists
        if (!destroytime && !deploytime) {
            return null;
        }
        
        return (
            <div className="mt-1">
                <div style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                    borderRadius: '3px', 
                    padding: '1px 4px',
                    display: 'inline-block',
                    fontSize: '10px'
                }}>
                    {destroytime && (
                        <span style={{ color: '#d32f2f', fontWeight: '500' }}>🔻 {destroytime}</span>
                    )}
                    {destroytime && deploytime && (
                        <span style={{ color: '#ccc', margin: '0 3px' }}>•</span>
                    )}
                    {deploytime && (
                        <span style={{ color: '#388e3c', fontWeight: '500' }}>⚡ {deploytime}</span>
                    )}
                </div>
            </div>
        );
    } else if (state === CLUSTER_STATES.DEPLOYING && deploytimer) {
        return (
            <div className="mt-1">
                <div style={{ 
                    backgroundColor: 'rgba(255, 152, 0, 0.08)', 
                    borderRadius: '3px', 
                    padding: '1px 4px',
                    display: 'inline-block',
                    fontSize: '10px'
                }}>
                    <span style={{ color: '#f57c00', fontWeight: '500' }}>⏳ {deploytimer}</span>
                </div>
            </div>
        );
    } else if (state === CLUSTER_STATES.DESTROYING && destroytimer) {
        return (
            <div className="mt-1">
                <div style={{ 
                    backgroundColor: 'rgba(255, 152, 0, 0.08)', 
                    borderRadius: '3px', 
                    padding: '1px 4px',
                    display: 'inline-block',
                    fontSize: '10px'
                }}>
                    <span style={{ color: '#f57c00', fontWeight: '500' }}>🔻 {destroytimer}</span>
                </div>
            </div>
        );
    }
    return null;
}

export const CLUSTER_STATES = {
    DEPLOYED: 'available',
    DEPLOYING: 'deploying',
    DESTROYED: 'down',
    STOPPING: 'stopping',
    STOPPED: 'stopped',
    STARTING: 'starting',
    DESTROYING: 'destroying',
    FAILED: 'failed',
    OFFLINE: 'offline',
}

export const PROVIDERS = {
    AWS: 'aws',
    GCP: 'gcp',
    MAAS: 'maas',
    AZURE: 'azure',
    OCI: 'oracle',
    IBM: 'ibm',
};

export const FSR_STATE = {
	ENABLED: 'enabled',
	DISABLED: 'disabled',
	ENABLING: 'enabling',
	DISABLING: 'disabling',
	NONE: 'none',
};

export const FSR_ACTIONS = {
    ENABLE: 'enable',
    DISABLE: 'disable',
};